import { AgeRange, Gender, LocationType, Scores, Strands } from '../../../generated/graphql'
import { TranslateService } from '@ngx-translate/core'
import { AppInjector } from '../../app.module'
import { AbstractControl, UntypedFormGroup, ValidationErrors } from '@angular/forms'
import { Injectable, OnDestroy } from '@angular/core'
import { Subject } from 'rxjs'

@Injectable()
export class BaseComponent implements OnDestroy {
  readonly dateTimeFormat = `MMM d, y, HH:mm:ss`
  readonly dateFormat = `MMM d, y`
  readonly ageRanges = Object.keys(AgeRange)
  readonly genders = Object.keys(Gender)
  readonly locationTypes = Object.keys(LocationType)
  readonly scienceOrganisationLocationTypes = Object.keys(LocationType).filter(
    (x) => x !== LocationType.ScienceOrganisation,
  )
  // readonly languages = ['en', 'de', 'fr', 'es', 'it', 'pt', 'ru', 'nl', 'el', 'he', 'pl'].sort()
  readonly languages = ['en', 'de', 'fr', 'es', 'it', 'pt', 'ru', 'nl', 'el', 'he', 'pl', 'no', 'ja']

  readonly minimumPasswordLength = 8

  readonly emptyScore: Scores = {
    currentStreak: 0,
    longestStreak: 0,
    doScore: 0,
    readScore: 0,
    visitAttendScore: 0,
    watchListenScore: 0,
    strandsScores: {
      interesting: 0,
      sciencePerson: 0,
      scienceToolsAndLanguage: 0,
      insight: 0,
      understanding: 0,
      learned: 0,
    },
  }
  readonly emptyStrands: Strands = {
    interesting: 0,
    learned: 0,
    understanding: 0,
    insight: 0,
    scienceToolsAndLanguage: 0,
    sciencePerson: 0,
  }

  protected translateService: TranslateService

  protected unsubscribe$ = new Subject<void>()

  constructor() {
    const injector = AppInjector.getInjector()
    this.translateService = injector.get(TranslateService)
  }

  getErrorMessage(formGroup: AbstractControl | null | undefined, formControlName: string) {
    const formControl = (formGroup as UntypedFormGroup).get(formControlName)
    let errorMsg = ''
    if (!formControl) {
      console.log(`FormControl [${formControlName}] is not found - handle this with an error later.`)
      return ''
    }
    if (formControl && formControl.errors) {
      Object.keys(formControl.errors as ValidationErrors).forEach((error) => {
        switch (error) {
          case 'minlength':
            errorMsg = this.getTranslatedLength(error, formControl.errors?.minlength.requiredLength)
            break
          case 'maxlength':
            errorMsg = this.getTranslatedLength(error, formControl.errors?.maxlength.requiredLength)
            break
          default:
            errorMsg = this.getTranslatedError(error)
        }
        if (!errorMsg) {
          errorMsg = this.getTranslatedError(error)
        }
      })
      return errorMsg
    }
    return ''
  }

  getTranslatedError(key: string) {
    return `${this.getTranslationFromKey(key)}`
  }

  getTranslatedLength(key: string, length: number) {
    return `${this.getTranslationFromKey(key)}${length}`
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }

  protected isObjectEmpty(obj: any) {
    return obj && Object.keys(obj).length === 0 && Object.getPrototypeOf(obj) === Object.prototype
  }

  protected isObject(entity: any) {
    return typeof entity === 'object' && !Array.isArray(entity) && entity !== null
  }

  protected removeTypenamesFromEntity(entity: any) {
    const clone = Object.assign({}, entity)
    for (const key in clone) {
      if (key == '__typename') {
        delete clone['__typename']
      }
      if (clone.hasOwnProperty(key)) {
        if (this.isObject(clone[key])) {
          clone[key] = this.removeTypenamesFromEntity(clone[key])
        }
        if (Array.isArray(clone[key])) {
          clone[key] = clone[key].map((entry: any) => {
            if (this.isObject(entry)) {
              return this.removeTypenamesFromEntity(entry)
            }
            return entry
          })
        }
      }
    }
    return clone
  }

  private getTranslationFromKey(key: string) {
    return this.translateService.instant(`errors.${key}`)
  }
}
